import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransactions, useUI } from '../../hooks';
import Text from '../../components/Text/Text';
import TransactionItem from '../../components/TransactionItem/TransactionItem';
import { Transaction } from '../../types';
import './AdminTransactionsScreen.css';

/**
 * AdminTransactionsScreen component
 * 
 * This component displays a list of transactions for admin users.
 * It allows filtering between all transactions and incomplete transactions.
 * Admins can also release funds for eligible transactions.
 */
const AdminTransactionsScreen: React.FC = () => {
  const navigate = useNavigate();
  const {
    transactions,
    getAdminTransactions,
    releasePayment,
    isLoading,
    error,
    pagination
  } = useTransactions();
  const { showToast } = useUI();
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [disputedTransactions, setDisputedTransactions] = useState<Transaction[]>([]);
  
  // Use a ref to track if transactions have been fetched
  const hasLoadedTransactions = useRef(false);
  
  // Fetch transactions only once on component mount or when filter changes
  useEffect(() => {
    // Only fetch if we haven't already loaded transactions
    if (!hasLoadedTransactions.current) {
      const fetchTransactions = async () => {
        try {
          // Use the admin-specific method to get transactions
          await getAdminTransactions(showIncompleteOnly, 1, 10);
          // Mark as loaded
          hasLoadedTransactions.current = true;
        } catch (error) {
          showToast('error', 'Failed to load transactions');
        }
      };
      
      fetchTransactions();
    }
  }, [getAdminTransactions, showIncompleteOnly, showToast]);
  
  // Filter disputed transactions
  useEffect(() => {
    if (transactions) {
      setDisputedTransactions(transactions.filter(t => t.isDisputed));
    }
  }, [transactions]);
  
  // Handle transaction click
  const handleTransactionClick = useCallback((transaction: Transaction) => {
    navigate(`/payment/${transaction.id}`);
  }, [navigate]);
  
  // Handle release funds
  const handleReleaseFunds = useCallback(async (transaction: Transaction, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation
    
    try {
      await releasePayment(transaction.id);
      showToast('success', 'Funds released successfully');
      
      // Refresh the transactions list
      hasLoadedTransactions.current = false;
      await getAdminTransactions(showIncompleteOnly, 1, 10);
      hasLoadedTransactions.current = true;
    } catch (error) {
      showToast('error', 'Failed to release funds');
    }
  }, [releasePayment, getAdminTransactions, showIncompleteOnly, showToast]);
  
  // Handle load more
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !pagination.hasMore) return;
    
    setLoadingMore(true);
    try {
      await getAdminTransactions(showIncompleteOnly, pagination.currentPage + 1, pagination.limit);
    } catch (error) {
      showToast('error', 'Failed to load more transactions');
    } finally {
      setLoadingMore(false);
    }
  }, [getAdminTransactions, loadingMore, pagination.currentPage, pagination.hasMore, pagination.limit, showIncompleteOnly, showToast]);
  
  // Handle filter change
  const handleFilterChange = useCallback(() => {
    setShowIncompleteOnly(!showIncompleteOnly);
    hasLoadedTransactions.current = false;
  }, [showIncompleteOnly]);
  
  // No need to filter transactions here as the API already does the filtering
  const filteredTransactions = transactions;
  
  // Render transaction item
  const renderTransactionItem = useCallback((transaction: Transaction) => {
    // Determine status text and color
    let statusText = '';
    let statusColor = '';
    
    if (transaction.status === 'disputed') {
      statusText = 'Disputed';
      statusColor = 'text-danger';
    } else if (transaction.isDisputed) {
      // Also check the isDisputed flag for transactions that might be in another status but have an active dispute
      statusText = 'Disputed';
      statusColor = 'text-danger';
    } else if (transaction.status === 'in_escrow') {
      statusText = 'In Escrow';
      statusColor = 'text-primary';
    } else if (transaction.status === 'released') {
      statusText = 'Released';
      statusColor = 'text-success';
    } else if (transaction.status === 'pending') {
      statusText = 'Pending';
      statusColor = 'text-warning';
    } else if (transaction.status === 'refunded') {
      statusText = 'Refunded';
      statusColor = 'text-info';
    } else if (transaction.status === 'cancelled') {
      statusText = 'Cancelled';
      statusColor = 'text-danger';
    }
    
    // Determine if we can release funds
    const canReleaseFunds = transaction.status === 'in_escrow' && transaction.trackingId;
    
    // Create status element
    const statusElement = (
      <Text className={`text-caption ${statusColor}`}>
        {transaction.trackingId ? `${statusText} | Tracking Provided` : `${statusText} | No Tracking`}
      </Text>
    );
    
    // Create action element
    let actionElement = null;
    
    if (canReleaseFunds) {
      actionElement = (
        <button
          className="release-funds-tag"
          onClick={(e) => handleReleaseFunds(transaction, e)}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none">
            <circle cx="12" cy="12" r="8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 8v8M9 12h6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 7l4 -4M16 7h4M16 7v4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Release Funds
        </button>
      );
    }
    
    // Get buyer and seller public user IDs
    const buyerPublicId = transaction.buyerPublicId || 'Unknown';
    const sellerPublicId = transaction.sellerPublicId || 'Unknown';
    console.log("Transaction item from admin listing:", transaction);


    return (
      <TransactionItem
        key={transaction.id}
        role="admin"
        itemDescription={`${transaction.itemDescription}`}
        itemPrice={`$${transaction.totalAmount?.toFixed(2) || '0.00'}`}
        itemBuyer={buyerPublicId}
        itemSeller={sellerPublicId}
        status={statusElement}
        action={actionElement}
        priceTagStyle="ribbon-right"
        onClick={() => handleTransactionClick(transaction)}
        transaction={transaction}
      />
    );
  }, [handleReleaseFunds, handleTransactionClick]);
  
  return (
    <div className="admin-transactions-container">
      <div className="admin-transactions-header">
        <Text className="text-h2">Transactions Management</Text>
        <div className="filter-controls">
          <label className="filter-toggle">
            <input
              type="checkbox"
              checked={showIncompleteOnly}
              onChange={handleFilterChange}
            />
            <span className="toggle-label">Show incomplete transactions only</span>
          </label>
        </div>
      </div>
      
      <div className="admin-transactions-list">
        {isLoading && !loadingMore ? (
          <div className="loading-state">
            <Text>Loading transactions...</Text>
          </div>
        ) : error ? (
          <div className="error-state">
            <Text className="text-error">Failed to load transactions</Text>
            <button
              className="retry-button"
              onClick={() => {
                hasLoadedTransactions.current = false;
                getAdminTransactions(showIncompleteOnly, 1, 10);
              }}
            >
              Retry
            </button>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg viewBox="0 0 24 24" width="48" height="48" stroke="#6c757d" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="3" y1="9" x2="21" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="9" y1="21" x2="9" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <Text className="text-body">No transactions found</Text>
            <Text className="text-caption text-gray">
              {showIncompleteOnly 
                ? "There are no incomplete transactions in the system" 
                : "There are no transactions in the system"}
            </Text>
          </div>
        ) : (
          <>
            {filteredTransactions.map(transaction => (
              <div key={transaction.id}>
                {renderTransactionItem(transaction)}
              </div>
            ))}
            
            {pagination.hasMore && (
              <button
                className="admin-view-more-button"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Show More'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminTransactionsScreen;